import { produce } from 'immer';
import { calcTrajectory } from '../../utils/trajectoryUtils';
import { ADD_BOMB, ADD_BOMB_SPLAT, CLEAR_BOMBS_STATE, RECALCULATE_BOMB_TRAJECTORIES, REMOVE_ALL_BOMB_SPLATS, REMOVE_ALL_PLAYER_BOMB_SPLATS, REMOVE_BOMB, REMOVE_BOMB_SPLAT, SET_DRAGGING_BOMB_IS_OVER_TARGET, SET_DRAGGING_BOMB_TYPE } from '../actions/bombsActions';
import { getIncrementedId } from './utils/reducerUtils';
const initialState = {
    nextBombId: 0,
    nextBombSplatId: 0,
    bombs: [],
    bombSplats: {}
};
function draftGetAndIncrementBombId(draft) {
    const currBombId = draft.nextBombId;
    draft.nextBombId = getIncrementedId(draft.nextBombId);
    return currBombId;
}
function draftGetAndIncrementBombSplatId(draft) {
    const currBombSplatId = draft.nextBombSplatId;
    draft.nextBombSplatId = getIncrementedId(draft.nextBombSplatId);
    return currBombSplatId;
}
export function getFromX(fromSeatBounds, toSeatBounds) {
    if (Math.abs(fromSeatBounds.x - toSeatBounds.x) < 4) {
        // trajectory is almost straight up or straight down, randomly fire from left or right of seat center:
        return fromSeatBounds.x + ((Math.random() > 0.5 ? 1 : -1) * fromSeatBounds.w) / 4;
    }
    return fromSeatBounds.x;
}
const bombsReducer = (state = initialState, action) => {
    return produce(state, (draft) => {
        switch (action.type) {
            case CLEAR_BOMBS_STATE: {
                return initialState;
            }
            case ADD_BOMB: {
                const { fromSeatIndex, toSeatIndex, toSeatBounds, fromCoords, trajectory, bombType, tableAreaDimensions } = action.payload;
                draft.bombs.push({
                    id: draftGetAndIncrementBombId(draft),
                    bombType,
                    startTimestampMs: performance.now(),
                    fromSeatIndex,
                    fromX: fromCoords.x,
                    fromY: fromCoords.y,
                    toSeatIndex,
                    toX: toSeatBounds.x,
                    toY: toSeatBounds.y,
                    trajectory,
                    tableAreaDimensions
                });
                break;
            }
            case ADD_BOMB_SPLAT: {
                const bombSplat = action.payload;
                let seatBombSplats = draft.bombSplats[bombSplat.seatIndex];
                if (!seatBombSplats) {
                    seatBombSplats = [];
                    draft.bombSplats[bombSplat.seatIndex] = seatBombSplats;
                }
                seatBombSplats.push({
                    ...bombSplat,
                    id: draftGetAndIncrementBombSplatId(draft)
                });
                break;
            }
            case REMOVE_BOMB: {
                draft.bombs = draft.bombs.filter((bomb) => bomb.id !== action.payload);
                break;
            }
            case REMOVE_BOMB_SPLAT: {
                const { seatIndex, id } = action.payload;
                const newBombSplats = (draft.bombSplats[seatIndex] || []).filter((bombSplat) => bombSplat.id !== id);
                if (newBombSplats.length) {
                    draft.bombSplats[seatIndex] = newBombSplats;
                }
                else {
                    delete draft.bombSplats[seatIndex];
                }
                break;
            }
            case REMOVE_ALL_PLAYER_BOMB_SPLATS: {
                const seatIndex = action.payload;
                delete draft.bombSplats[seatIndex];
                break;
            }
            case REMOVE_ALL_BOMB_SPLATS: {
                draft.bombSplats = {};
                break;
            }
            case RECALCULATE_BOMB_TRAJECTORIES: {
                /**
                 * If the stage dimensions change (on resize or if the active panel is toggled on and off),
                 * recalculate all in-flight bomb trajectories so each will have a new path to the destination seat.
                 */
                const { allSeatBounds, tableAreaDimensions } = action.payload;
                draft.bombs = draft.bombs.map((bomb) => {
                    const toSeatBounds = allSeatBounds[bomb.toSeatIndex];
                    // Scale the "from" coordinates based on how the table area dimensions are changing (if they are changing)
                    // relative to what was stored on the initial bomb object. This way, if the width of the table area changes
                    // (for example, closing the panel), the reclaculated bomb will appear to be coming from the same relative
                    // spot on the screen rather than recalculating to something totally random:
                    const fromSeatBounds = {
                        x: (bomb.fromX * tableAreaDimensions.w) / bomb.tableAreaDimensions.w,
                        y: (bomb.fromY * tableAreaDimensions.h) / bomb.tableAreaDimensions.h,
                        w: toSeatBounds.w,
                        h: toSeatBounds.h
                    };
                    const fromCoords = {
                        x: getFromX(fromSeatBounds, toSeatBounds),
                        y: fromSeatBounds.y
                    };
                    const trajectory = calcTrajectory(fromCoords, toSeatBounds);
                    if (!trajectory) {
                        console.warn('Could not recalculate bomb trajectory');
                        // just return the in-flight bomb as it was:
                        return bomb;
                    }
                    const id = draftGetAndIncrementBombId(draft);
                    return {
                        ...bomb,
                        fromX: fromCoords.x,
                        fromY: fromCoords.y,
                        toX: toSeatBounds.x,
                        toY: toSeatBounds.y,
                        id,
                        trajectory,
                        tableAreaDimensions
                    };
                });
                break;
            }
            case SET_DRAGGING_BOMB_TYPE: {
                const type = action.payload;
                if (type) {
                    draft.draggingBomb = { type };
                }
                else {
                    delete draft.draggingBomb;
                }
                break;
            }
            case SET_DRAGGING_BOMB_IS_OVER_TARGET: {
                if (draft.draggingBomb) {
                    draft.draggingBomb.isOverTarget = action.payload;
                }
                break;
            }
            default: {
                return state;
            }
        }
    });
};
export default bombsReducer;
